import React from 'react';
import { cn } from '../../utils/cn';

/**
 * ช่องข้อมูลแบบอ่านอย่างเดียว (ป้าย + ค่า) ของหน้ารายละเอียด — แทน input ที่ disabled ในโมดัลเดิม
 * หน้าตาเดียวกับ <dl> ของ MaintenanceViewPage
 */

/** ค่า "ว่าง" ที่ต้องแสดงเป็น '-' (0 / false ไม่ว่าง — เป็นค่าจริง) */
export const isEmptyValue = (value: unknown): boolean =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

export interface InfoFieldProps {
  label: React.ReactNode;
  /** ค่าที่แสดง · ว่าง (null/undefined/'') = '-' · ส่ง children แทนได้เมื่อต้องจัดรูปเอง (badge/ลิงก์) */
  value?: React.ReactNode;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  /** ตัวอักษร monospace (รหัส/เลขผู้เสียภาษี/เบอร์) */
  mono?: boolean;
  /** ข้อความหลายบรรทัด (ที่อยู่/หมายเหตุ) — คงการขึ้นบรรทัด + ตัดคำยาว */
  multiline?: boolean;
  /** กินเต็มแถวของ InfoGrid */
  fullWidth?: boolean;
  /** คำอธิบายเล็กใต้ค่า */
  hint?: React.ReactNode;
  className?: string;
  testId?: string;
}

export const InfoField: React.FC<InfoFieldProps> = ({
  label, value, children, icon, mono, multiline, fullWidth, hint, className, testId,
}) => {
  const content = children !== undefined ? children : value;
  const empty = children === undefined && isEmptyValue(value);
  return (
    <div className={cn('min-w-0', fullWidth && 'sm:col-span-2 lg:col-span-full', className)} data-testid={testId}>
      <dt className="text-sm font-medium text-secondary-500 mb-1 flex items-center gap-1">
        {icon}
        {label}
      </dt>
      <dd
        className={cn(
          'text-sm text-secondary-900 [overflow-wrap:anywhere]',
          mono && 'font-mono',
          multiline && 'whitespace-pre-wrap [overflow-wrap:anywhere]'
        )}
      >
        {empty ? <span className="text-secondary-400">-</span> : content}
      </dd>
      {hint && <p className="mt-1 text-xs text-secondary-500">{hint}</p>}
    </div>
  );
};

export interface InfoGridProps {
  children: React.ReactNode;
  /** จำนวนคอลัมน์บนจอใหญ่ (มือถือ 1 คอลัมน์เสมอ) */
  columns?: 1 | 2 | 3;
  className?: string;
}

export const InfoGrid: React.FC<InfoGridProps> = ({ children, columns = 2, className }) => (
  <dl
    className={cn(
      'grid grid-cols-1 gap-x-6 gap-y-4',
      columns >= 2 && 'sm:grid-cols-2',
      columns === 3 && 'lg:grid-cols-3',
      className
    )}
  >
    {children}
  </dl>
);

export default InfoField;
